import { Injectable, NgZone, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MobileDatepickerService implements OnDestroy {
  isMobile: boolean = false;
  monthsToShow: number = 3;

  private lastWidth: number = 0;
  private resizeListener?: () => void;
  private capturingListener?: (event: Event) => void;
  private isNavigating: boolean = false;
  private navTimer?: any;

  constructor(private zone: NgZone) {
    if (typeof window !== 'undefined') {
      this.lastWidth = window.innerWidth;
      this.calculateBreakpoints(this.lastWidth);
      this.setupResizeListener();
      this.setupCapturingListeners();
    }
  }

  private calculateBreakpoints(width: number): boolean {
    const prevIsMobile = this.isMobile;
    const prevMonths = this.monthsToShow;

    if (width < 768) {
      this.isMobile = true;
      this.monthsToShow = 1;
    } else if (width < 1024) {
      this.isMobile = false;
      this.monthsToShow = 2;
    } else {
      this.isMobile = false;
      this.monthsToShow = 3;
    }

    return prevIsMobile !== this.isMobile || prevMonths !== this.monthsToShow;
  }

  private setupResizeListener() {
    this.zone.runOutsideAngular(() => {
      this.resizeListener = () => {
        const currentWidth = window.innerWidth;
        // Ignore height-only resize events (such as mobile browser address bar showing/hiding)
        if (currentWidth !== this.lastWidth) {
          this.lastWidth = currentWidth;
          const changed = this.calculateBreakpoints(currentWidth);
          if (changed) {
            this.zone.run(() => {
              // triggers Angular change detection when breakpoint changes
            });
          }
        }
      };
      window.addEventListener('resize', this.resizeListener, { passive: true });
    });
  }

  private setupCapturingListeners() {
    if (typeof document === 'undefined') return;

    // Capture mousedown, touchstart, and pointerdown to intercept month navigation
    this.capturingListener = (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target &&
        target.closest(
          '.p-datepicker-header, .p-datepicker-prev-button, .p-datepicker-next-button, .p-datepicker-title, .p-datepicker-select-month, .p-datepicker-select-year, .p-datepicker-decade'
        )
      ) {
        // Prevent event from bubbling to PrimeNG's documentClickListener which erroneously treats
        // re-rendered navigator buttons as outside clicks
        event.stopPropagation();
        this.isNavigating = true;
        if (this.navTimer) clearTimeout(this.navTimer);
        this.navTimer = setTimeout(() => {
          this.isNavigating = false;
        }, 500);
      }
    };

    document.addEventListener('mousedown', this.capturingListener, true);
    document.addEventListener('touchstart', this.capturingListener, { capture: true, passive: false });
    document.addEventListener('pointerdown', this.capturingListener, true);
  }

  /**
   * Protect a PrimeNG DatePicker instance so that calls to hideOverlay()
   * during month navigation (triggered by internal focus or scrollHandler)
   * are safely blocked.
   */
  protectDatepicker(dp: any) {
    if (!dp) return;
    let instance = dp.datepicker || dp;

    // If an HTMLElement was passed (e.g. from $event), try to find the DatePicker component
    if (instance instanceof HTMLElement && (instance as any).__ngContext__) {
      const ctx = (instance as any).__ngContext__;
      if (Array.isArray(ctx)) {
        for (const item of ctx) {
          if (item && typeof item.hideOverlay === 'function') {
            instance = item;
            break;
          }
        }
      }
    }

    if (instance && typeof instance.hideOverlay === 'function') {
      if (instance.touchUI && instance.scrollHandler) {
        instance.scrollHandler.unbindScrollListener();
      }
      if (!instance._mobileProtected) {
        instance._mobileProtected = true;
        const originalHide = instance.hideOverlay.bind(instance);
        instance.hideOverlay = () => {
          if (instance.isMonthNavigate || this.isNavigating) {
            return; // Block unwanted hideOverlay during month transitions
          }
          originalHide();
        };

        if (typeof instance.bindScrollListener === 'function') {
          const originalBindScroll = instance.bindScrollListener.bind(instance);
          instance.bindScrollListener = () => {
            if (instance.touchUI) {
              return; // Never close touchUI modal on scroll
            }
            originalBindScroll();
          };
        }
      }
    }
  }

  /**
   * Fail-safe helper to ensure body scroll is unlocked when calendar closes
   */
  ensureScrollUnlocked() {
    if (typeof document === 'undefined') return;
    setTimeout(() => {
      const activeMasks = document.querySelectorAll('.p-datepicker-mask-scrollblocker');
      if (activeMasks.length === 0) {
        document.body.classList.remove('p-overflow-hidden');
        document.body.style.removeProperty('--scrollbar-width');
      }
    }, 100);
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined' && this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
    if (typeof document !== 'undefined' && this.capturingListener) {
      document.removeEventListener('mousedown', this.capturingListener, true);
      document.removeEventListener('touchstart', this.capturingListener, true);
      document.removeEventListener('pointerdown', this.capturingListener, true);
    }
    if (this.navTimer) clearTimeout(this.navTimer);
  }
}
